import { useSyncExternalStore, useCallback, useRef } from 'react'
import { provider } from '@/shared/config/yjs-config'
import type { Awareness } from 'y-protocols/awareness'

// ==================== Types ====================

export interface NodeTransientState {
  iu?: boolean // isUploading
  ie?: boolean // isEditing
}

export interface UserInfo {
  uid: string
}

export interface CursorState {
  x: number
  y: number
}

export interface AwarenessState {
  ns?: Record<string, NodeTransientState>
  u?: string
  x?: number
  y?: number
  evts?: Record<string, number> // 泛型广播事件记录，格式如 { 'rtm': 169000000000 }
}

// ==================== Core API ====================

export function getAwareness(): Awareness {
  if (!provider) {
    throw new Error('Yjs provider is not connected')
  }
  return provider.awareness!
}

export function getLocalAwarenessState(): AwarenessState {
  return (getAwareness().getLocalState() as AwarenessState) ?? {}
}

export let isAwarenessSyncDisabled = false;

export function setAwarenessSyncDisabled(disabled: boolean) {
  isAwarenessSyncDisabled = disabled;
  if (disabled) {
    try {
      if (provider && provider.awareness) {
        provider.awareness.setLocalState(null);
      }
    } catch (e) { }
  }
}

export function setLocalAwarenessField<K extends keyof AwarenessState>(
  field: K,
  value: AwarenessState[K],
): void {
  if (isAwarenessSyncDisabled) return;
  getAwareness().setLocalStateField(field, value)
}

export function setLocalUserInfo(user: UserInfo): void {
  setLocalAwarenessField('u', user.uid)
}

export function setLocalCursor(cursor: CursorState): void {
  if (isAwarenessSyncDisabled) return;
  const current = getLocalAwarenessState()
  getAwareness().setLocalState({
    ...current,
    x: cursor.x,
    y: cursor.y,
  })
}

export function clearLocalCursor(): void {
  if (isAwarenessSyncDisabled) return;
  const current = getLocalAwarenessState()
  const next = { ...current }
  delete next.x
  delete next.y
  getAwareness().setLocalState(next)
}

export function getLocalClientId(): number {
  return getAwareness().clientID
}

export function getAllAwarenessStates(): Map<number, AwarenessState> {
  return getAwareness().getStates() as Map<number, AwarenessState>
}

// ==================== Field-level subscriptions ====================

const EMPTY_MAP = new Map<number, unknown>()

/**
 * 订阅所有 awareness states（基础 hook，上层按需封装字段级 hook）
 */
export function useAwareness<T>(selector: (states: Map<number, AwarenessState>) => T): T {
  const awareness = getAwareness()

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      awareness.on('change', onStoreChange)
      return () => { awareness.off('change', onStoreChange) }
    },
    [awareness],
  )

  const getSnapshot = useCallback(
    () => selector(awareness.getStates() as Map<number, AwarenessState>),
    [awareness, selector],
  )

  return useSyncExternalStore(subscribe, getSnapshot)
}

/**
 * 只订阅 cursor + user 字段（用于 RemoteCursors）
 * 仅在 cursor 或 user 字段发生变化时触发 re-render
 */
export function useAwarenessCursor(): Map<number, { user?: UserInfo; cursor?: CursorState }> {
  const cachedRef = useRef<Map<number, { user?: UserInfo; cursor?: CursorState }>>(EMPTY_MAP as Map<number, any>)

  const selector = useCallback(
    (states: Map<number, AwarenessState>): Map<number, { user?: UserInfo; cursor?: CursorState }> => {
      const prev = cachedRef.current
      const next = new Map<number, { user?: UserInfo; cursor?: CursorState }>()

      for (const [id, state] of states) {
        if (state.u || (state.x !== undefined && state.y !== undefined)) {
          next.set(id, {
            user: state.u ? { uid: state.u } : undefined,
            cursor: state.x !== undefined && state.y !== undefined ? { x: state.x, y: state.y } : undefined,
          })
        }
      }

      if (prev.size === next.size && prev !== EMPTY_MAP) {
        let changed = false
        for (const [id, nextEntry] of next) {
          const prevEntry = prev.get(id)
          if (
            !prevEntry ||
            prevEntry.cursor?.x !== nextEntry.cursor?.x ||
            prevEntry.cursor?.y !== nextEntry.cursor?.y ||
            prevEntry.user?.uid !== nextEntry.user?.uid
          ) {
            changed = true
            break
          }
        }
        if (!changed) return prev
      }

      cachedRef.current = next
      return next
    },
    [],
  )

  return useAwareness(selector)
}

export function useAwarenessUsers(): Map<number, UserInfo> {
  const cachedRef = useRef<Map<number, UserInfo>>(EMPTY_MAP as Map<number, any>)

  const selector = useCallback(
    (states: Map<number, AwarenessState>): Map<number, UserInfo> => {
      const prev = cachedRef.current
      const next = new Map<number, UserInfo>()

      for (const [id, state] of states) {
        if (state.u) {
          next.set(id, { uid: state.u })
        }
      }

      if (prev.size === next.size && prev !== EMPTY_MAP) {
        let changed = false
        for (const [id, nextUser] of next) {
          const prevUser = prev.get(id)
          if (!prevUser || prevUser.uid !== nextUser.uid) {
            changed = true
            break
          }
        }
        if (!changed) return prev
      }

      cachedRef.current = next
      return next
    },
    [],
  )

  return useAwareness(selector)
}

export function broadcastAwarenessEvent(eventName: string) {
  try {
    const awareness = getAwareness()
    if (awareness && awareness.getStates().size > 1) {
      const currentState = awareness.getLocalState() as AwarenessState
      const evts = currentState?.evts || {}
      awareness.setLocalStateField('evts', {
        ...evts,
        [eventName]: Date.now()
      })
    }
  } catch (e) {
    console.warn(`Failed to broadcast event ${eventName} via awareness`, e)
  }
}

/**
 * 监听某个泛型广播事件的最新触发时间戳
 */
export function useAwarenessEvent(eventName: string): number {
  return useAwareness((states) => {
    let latest = 0
    states.forEach(state => {
      if (state.evts && state.evts[eventName] && state.evts[eventName] > latest) {
        latest = state.evts[eventName]
      }
    })
    return latest
  })
}
